import { cn } from "@/engine/support/classNames";
import { useHydrated } from "@/hooks/useHydrated";

/**
 * The night the landing stands in (#962).
 *
 * A layer fixed behind the whole page: a sky with a few stars that twinkle, a
 * warm glow from the edges as if from a fire out of frame, and fireflies
 * drifting slowly. Every colour is a palette token, so it follows the visitor's
 * palette; in a light one the stars are dust and the glow is sunlight, which is
 * what a light palette asks for. It takes no pointer and says nothing to
 * assistive technology, and under reduced motion nothing in it moves.
 *
 * It is drawn after hydration, not in the server's document: the landing's
 * slow-connection first paint has little room under its ceiling, and scenery
 * is the last thing a visitor needs in it.
 */

/** Where the fireflies start, in percent of the viewport, and how they move. */
const FIREFLIES: readonly {
  left: number;
  top: number;
  delay: number;
  size: number;
}[] = [
  { left: 6, top: 18, delay: 0, size: 4 },
  { left: 14, top: 62, delay: -3, size: 3 },
  { left: 22, top: 34, delay: -7, size: 3 },
  { left: 31, top: 82, delay: -2, size: 4 },
  { left: 38, top: 12, delay: -9, size: 3 },
  { left: 47, top: 54, delay: -5, size: 3 },
  { left: 55, top: 26, delay: -11, size: 4 },
  { left: 63, top: 74, delay: -4, size: 3 },
  { left: 71, top: 40, delay: -8, size: 3 },
  { left: 78, top: 88, delay: -1, size: 4 },
  { left: 85, top: 16, delay: -6, size: 3 },
  { left: 92, top: 58, delay: -10, size: 4 },
  { left: 97, top: 32, delay: -12, size: 3 },
  { left: 3, top: 90, delay: -13, size: 3 },
];

/** Two tiles of stars at different sizes, so the pattern never reads as a grid. */
const STARS_NEAR =
  "radial-gradient(1.4px 1.4px at 22px 34px, hsl(var(--foreground) / 0.55), transparent), radial-gradient(1px 1px at 120px 80px, hsl(var(--foreground) / 0.45), transparent), radial-gradient(1.2px 1.2px at 70px 150px, hsl(var(--foreground) / 0.5), transparent)";
const STARS_FAR =
  "radial-gradient(1px 1px at 40px 60px, hsl(var(--foreground) / 0.35), transparent), radial-gradient(1px 1px at 210px 30px, hsl(var(--foreground) / 0.3), transparent), radial-gradient(1px 1px at 150px 220px, hsl(var(--foreground) / 0.3), transparent), radial-gradient(1px 1px at 260px 170px, hsl(var(--foreground) / 0.25), transparent)";

export function LandingBackdrop() {
  if (!useHydrated()) return null;
  return (
    <div
      aria-hidden="true"
      data-testid="landing-backdrop"
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
    >
      <span className="absolute inset-0 bg-[radial-gradient(70%_55%_at_100%_0%,hsl(var(--primary)/0.12),transparent_70%),radial-gradient(60%_50%_at_0%_45%,hsl(var(--accent)/0.08),transparent_70%),radial-gradient(80%_45%_at_50%_110%,hsl(var(--primary)/0.10),transparent_70%)]" />
      <span
        className="absolute inset-0 opacity-40 dark:opacity-100"
        style={{ backgroundImage: STARS_FAR, backgroundSize: "300px 260px" }}
      />
      <span
        className="landing-twinkle absolute inset-0 opacity-40 [animation-duration:6s] dark:opacity-100"
        style={{ backgroundImage: STARS_NEAR, backgroundSize: "180px 190px" }}
      />
      {FIREFLIES.map(({ left, top, delay, size }) => (
        <span
          key={`${left}-${top}`}
          className={cn(
            "landing-drift absolute rounded-full bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.9)]",
          )}
          style={{
            left: `${left}%`,
            top: `${top}%`,
            width: size,
            height: size,
            animationDelay: `${delay}s`,
          }}
        />
      ))}
    </div>
  );
}

/** A line of pines between the first screen and the rest, in the page's own colours. */
export function LandingPines() {
  const trees = [
    [0, 58],
    [5, 74],
    [9, 52],
    [14, 86],
    [19, 64],
    [24, 92],
    [29, 70],
    [34, 56],
    [39, 80],
    [44, 62],
    [49, 96],
    [54, 68],
    [59, 84],
    [64, 58],
    [69, 90],
    [74, 66],
    [79, 78],
    [84, 54],
    [89, 88],
    [94, 70],
    [99, 60],
  ] as const;
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="pointer-events-none relative -z-[5] -mt-6 block h-20 w-full sm:-mt-10 sm:h-28"
    >
      {trees.map(([x, height]) => (
        <polygon
          key={x}
          points={`${x - 3.2},100 ${x},${100 - height} ${x + 3.2},100`}
          className="fill-[hsl(var(--card))] [&:nth-child(odd)]:fill-[hsl(var(--identity-moss)/0.16)]"
        />
      ))}
    </svg>
  );
}
